import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Col,
  Dropdown,
  Menu,
  Modal,
  ModalProps,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import copy from 'copy-text-to-clipboard';
import {
  ExportCSV,
  InfoButton,
  Label,
  ModalDialog,
  SpaceCell,
  SiteContent,
  message,
} from 'components/common';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  DATAGRID_CELL_ICON_WIDTH1,
} from 'constants/config';
import {
  closeWarehouseBatchTransfer,
  deleteWarehouseBatchTransfer,
  fetchWarehouseBatchTransferDetail,
  reopenWarehouseBatchTransfer,
} from 'services/inventory';
import { convertTimeByUtc } from 'utils';
import { batchStatusLabel, editTransferBatch, isErrorExist, submitTransferBatch } from '.';
import TransferLineGrid, { exportColumns, mapCsvData } from './TransferLineGrid';

type Props = ModalProps & {
  batchNumber: number;
  lockScreen?: Function;
  refreshList: Function;
  onSubmit?: Function;
};

const TransferBatchDetailDialog = (props: Props) => {
  const { useState } = React;
  const [inited, setInited] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [transferBatch, setTransferBatch] = useState<StringKAnyVPair>({});
  const history = useHistory();

  const attrLabelClass = () => {
    const cls = ['tail-colon2', 'text-end', 'w-35'];

    return cls.join(' ');
  };

  const closeTransfer = (batch: StringKAnyVPair) => {
    Modal.confirm({
      centered: true,
      content: `Are you sure to close this transfer(${batch.batchNumber})?`,
      title: 'Confirm',
      onOk: async () => {
        if (batch.warehouseBatchTransferNum > 0) {
          lockScreen(true);
          try {
            const res = await closeWarehouseBatchTransfer(batch.warehouseBatchTransferNum);

            //console.log('r', res);
            if (res) {
              //batchList.splice(idx, 1);
              /*batch.status = res.status;
              batch.updateBy = res.updateBy;
              batch.updateDate = res.updateDate;
              setBatchList([ ...batchList ]);*/
              // @ts-ignore
              props.onCancel && props.onCancel();
              props.refreshList();
              message.success({
                content: 'Closed successfully',
                duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
              });
            }
          } catch(e) {
            message.error({
              content: `Closed error: ${e}`,
              duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
            });
          } finally {
            lockScreen(false);
          }
        }
      },
    });
  };

  const deleteBatch = (batch: StringKAnyVPair) => {
    Modal.confirm({
      centered: true,
      content: `Are you sure to delete this transfer(${batch.batchNumber})?`,
      title: 'Confirm',
      onOk: async () => {
        if (batch.warehouseBatchTransferNum > 0) {
          lockScreen(true);
          try {
            const res = await deleteWarehouseBatchTransfer(batch.warehouseBatchTransferNum);

            //console.log('r', res);
            if (res) {
              /*batch.status = res.status;
              batch.updateBy = res.updateBy;
              batch.updateDate = res.updateDate;
              setBatchList([ ...batchList ]);*/
              // @ts-ignore
              props.onCancel && props.onCancel();
              props.refreshList();
              message.success({
                content: 'Deleted successfully',
                duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
              });
            }
          } catch(e) {
            message.error({
              content: `Deleted error: ${e}`,
              duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
            });
          } finally {
            lockScreen(false);
          }
        }
      },
    });
  };

  // eslint-disable-next-line
  const loadDetail = async (bn: number) => {
    lockScreen(true);
    try {
      const detail = await fetchWarehouseBatchTransferDetail(bn);

      if (detail) {
        console.log('d', detail);
        setTransferBatch(detail);
      }
    } catch(e) {
    } finally {
      lockScreen(false);
    }
  };

  const lockScreen = (lock: boolean) => {
    if (props.lockScreen) props.lockScreen(lock);
  };

  const onClose = () => {
    // @ts-ignore
    props.onCancel();
  };

  const onDialogFullscreen = (fullscreen: boolean) => {
    setIsFullscreen(fullscreen);
  };

  const reopenBatch = (batch: StringKAnyVPair) => {
    Modal.confirm({
      centered: true,
      content: `Are you sure to re-open this transfer(${batch.batchNumber})?`,
      title: 'Confirm',
      onOk: async () => {
        if (batch.warehouseBatchTransferNum > 0) {
          lockScreen(true);
          try {
            const res = await reopenWarehouseBatchTransfer(batch.warehouseBatchTransferNum);

            //console.log('r', res);
            if (res) {
              /*batch.status = res.status;
              batch.updateBy = res.updateBy;
              batch.updateDate = res.updateDate;
              setBatchList([ ...batchList ]);*/
              // @ts-ignore
              props.onCancel && props.onCancel();
              props.refreshList();
              message.success({
                content: 'Re-opened successfully',
                duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
              });
            }
          } catch(e) {
            message.error({
              content: `Re-opened error: ${e}`,
              duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
            });
          } finally {
            lockScreen(false);
          }
        }
      },
    });
  };

  const renderFooter = () => {
    return (
      <Row className="pt-8" justify="space-between">
        <Space>
          <InfoButton
            disabled={transferBatch.status !== 0}
            onClick={() => editTransferBatch(history, transferBatch.warehouseBatchTransferNum)}
          >
            <EditOutlined />
            Edit
          </InfoButton>
          {transferBatch.totalLine > 0 && (
            <ExportCSV
              columns={exportColumns}
              data={mapCsvData(transferBatch.lines)}
              scope={transferBatch.batchNumber}
            />
          )}
        </Space>
        <Space>
          {transferBatch.status === 0 && (
            <Dropdown
              overlay={(
                <Menu>
                  <Menu.Item disabled={transferBatch.status !== 0} key="close-batch-btn" onClick={() => closeTransfer(transferBatch)}>
                    <Label className="">
                      <CloseOutlined />
                      &nbsp;Close Transfer
                    </Label>
                  </Menu.Item>
                  <Menu.Item disabled={transferBatch.status !== 0} key="rm-batch-btn" onClick={() => deleteBatch(transferBatch)}>
                    <Label className="label-danger">
                      <DeleteOutlined />
                      &nbsp;Delete Transfer
                    </Label>
                  </Menu.Item>
                </Menu>
              )}
            >
              <Button className="p-5-8">
                <EllipsisOutlined />
              </Button>
            </Dropdown>
          )}
          {transferBatch.status === 6 && (
            <Dropdown
              overlay={(
                <Menu>
                  <Menu.Item disabled={transferBatch.status !== 6} key="reopen-batch-btn" onClick={() => reopenBatch(transferBatch)}>
                    <Label className="">
                      <FolderOpenOutlined />
                      &nbsp;Re-open Transfer
                    </Label>
                  </Menu.Item>
                </Menu>
              )}
            >
              <Button className="p-5-8">
                <EllipsisOutlined />
              </Button>
            </Dropdown>
          )}
          <Button
            disabled={transferBatch.status !== 0}
            onClick={submitBatch}
            type="primary"
          >
            <CheckOutlined />
            Submit
          </Button>
        </Space>
      </Row>
    );
  };

  const renderLineGrid = () => {
    return (<>
      <div
        className="mt-8 n-c-d-h-100"
        style={{height: `calc(100vh - ${isFullscreen ? 220 : 320}px)`}}
      >
        {transferBatch.warehouseBatchTransferNum && (
          <TransferLineGrid
            transferNumber={transferBatch.warehouseBatchTransferNum}
            //version={gridVersion}
          />
        )}
      </div>
    </>);
  };

  const renderSummary = () => {
    return (<>
      <Row>
        <Col span={8}>
          <Row align="middle">
            <Label className={attrLabelClass()}>Batch Number</Label>
            <Label className="label-grey">{transferBatch.batchNumber}</Label>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>Reference Number</Label>
            <Label className="label-grey">{transferBatch.referenceNumber}</Label>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>Status</Label>
            <Label className="label-grey">{batchStatusLabel(transferBatch.status)}</Label>
          </Row>
        </Col>
        <Col span={8}>
          <Row align="middle">
            <Label className="tail-colon2">Group Note</Label>
            <Label className="label-grey">{transferBatch.note}</Label>
          </Row>
          {isErrorExist(transferBatch) && (
            <Row align="middle" className="n-c-d-w-100">
              <SpaceCell
                icon={<>
                  <Tooltip
                    placement="top"
                    title="Copy error"
                    trigger={['hover', 'click']}
                  >
                    <CopyOutlined
                      className="text-info"
                      onClick={() => {
                        copy(transferBatch.error);
                        message.info({
                          content: 'Error has been copied to clipboard',
                          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
                        });
                      }}
                    />
                  </Tooltip>
                </>}
                iconWidth={DATAGRID_CELL_ICON_WIDTH1}
                text={(
                  <span className="text-danger">
                    <Label className="tail-colon2">Error</Label>
                    {transferBatch.error}
                  </span>
                )}
                //tooltip={typeof transferBatch.error === 'string' ? transferBatch.error.replace(/\n/g, '<br />') : transferBatch.error}
                tooltip={transferBatch.error}
              />
            </Row>
          )}
        </Col>
        <Col span={8}>
          <Row align="middle">
            <Label className={attrLabelClass()}>Create By</Label>
            <Typography.Text className="text-grey" ellipsis={{ tooltip: transferBatch.createBy }}>
              {transferBatch.createBy}
            </Typography.Text>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>Create Date</Label>
            <Label className="label-grey">{convertTimeByUtc(transferBatch.createDate)}</Label>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>Update By</Label>
            <Typography.Text className="text-grey w-60" ellipsis={{ tooltip: transferBatch.updateBy }}>
              {transferBatch.updateBy}
            </Typography.Text>
          </Row>
          <Row align="middle">
            <Label className={attrLabelClass()}>Update Date</Label>
            <Label className="label-grey">{convertTimeByUtc(transferBatch.updateDate)}</Label>
          </Row>
        </Col>
      </Row>
    </>);
  };

  const submitBatch = () => {
    submitTransferBatch(
      transferBatch,
      lockScreen,
      (batch: StringKAnyVPair) => {
        if (props.onSubmit) props.onSubmit(batch);

        message.success({
          content: 'Submitted successfully',
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
        onClose();
      },
    );
  };

  React.useEffect(() => {
    if (!inited) {
      loadDetail(props.batchNumber);
      setInited(true);
    }
  }, [inited, loadDetail, props]);

  return (<>
    <ModalDialog
      centered
      closeButton
      closable={false}
      destroyOnClose={true}
      footer={null}
      fullscreen
      maskClosable={false}
      onClose={onClose}
      onFullscreen={onDialogFullscreen}
      title="Group Transfer Detail"
      visible={props.visible}
      width={1200}
    >
      <SiteContent className="common-content">
        {transferBatch.warehouseBatchTransferNum > 0 && renderSummary()}
        {transferBatch.warehouseBatchTransferNum > 0 && renderLineGrid()}
        {transferBatch.warehouseBatchTransferNum > 0 && renderFooter()}
      </SiteContent>
    </ModalDialog>
  </>);
};

export default TransferBatchDetailDialog;
